import { moveAssetsToTrash } from "@apis/http/api";
import { ClipMoreMenu } from "@components/ClipMoreMenu/ClipMoreMenu";
import { IconLineTrash } from "@kalodata/kds-design-icons";
import { isNil, useSerializeCallback } from "@kalodata/tools";
import { Image as AntImage } from "antd";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { MediaPreviewImage } from "./MediaPreviewImage";

export interface ImagePreviewCardProps {
  src?: string;
  previewSrc?: string;
  alt?: string;
  className?: string;
  imageClassName?: string;
  disabledPreview?: boolean;
  /** 传入后展示更多菜单，并支持移入回收站 */
  assetId?: number;
  onDeleted?: () => void;
}

export const ImagePreviewCard = ({
  src,
  previewSrc,
  alt = "",
  className,
  imageClassName,
  disabledPreview = false,
  assetId,
  onDeleted,
}: ImagePreviewCardProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const resolvedPreviewSrc = previewSrc ?? src;
  const canPreview = Boolean(resolvedPreviewSrc) && !disabledPreview && !loadFailed;
  const showMoreMenu = typeof assetId === "number";

  const handleDelete = useSerializeCallback(async () => {
    if (isNil(assetId)) return;
    await moveAssetsToTrash({ ids: [assetId] });
    onDeleted?.();
  });

  const moreMenuOptions = useMemo(
    () => [
      {
        key: "delete",
        label: "Delete",
        icon: <IconLineTrash size={20} />,
        loading: handleDelete.isPending(),
        onClick: handleDelete,
      },
    ],
    [handleDelete],
  );

  const handleClick = useCallback(() => {
    if (!canPreview) return;
    setPreviewOpen(true);
  }, [canPreview]);

  const handleOpenChange = useCallback((open: boolean) => {
    setPreviewOpen(open);
  }, []);

  return (
    <>
      <ClipMoreMenu
        placement="bottomRight"
        className={classNames(
          "flex w-full items-center justify-center overflow-hidden rounded-[8px] bg-(--palette-slate-5)",
          className,
        )}
        options={showMoreMenu ? moreMenuOptions : []}
      >
        <button
          type="button"
          disabled={!canPreview}
          className={classNames(
            "relative flex h-full w-full items-center justify-center p-0",
            canPreview ? "cursor-pointer" : "cursor-default",
          )}
          onClick={handleClick}
        >
          <MediaPreviewImage src={src} alt={alt} imageClassName={imageClassName} onLoadFailedChange={setLoadFailed} />
        </button>
      </ClipMoreMenu>
      {resolvedPreviewSrc ? (
        <AntImage
          src={resolvedPreviewSrc}
          styles={{ root: { display: "none" } }}
          preview={{
            open: previewOpen,
            onOpenChange: handleOpenChange,
          }}
        />
      ) : null}
    </>
  );
};
